import type { ChangeEvent } from "react"
import BracketAllTrees from "@/components/bracket/BracketAllTrees"
import BracketOrderTable from "@/components/bracket/BracketOrderTable"
import BracketSportHeading from "@/components/bracket/BracketSportHeading"
import BracketTree from "@/components/bracket/BracketTree"
import EmptyMessage from "@/components/common/EmptyMessage"
import Loading from "@/components/common/Loading"
import { useBracket } from "@/domain/bracket/hooks/BracketHook"
import type { BracketViewMode } from "@/domain/bracket/hooks/BracketHook"
import { groupBySport } from "@/domain/bracket/utils/BracketUtils"

/** 화면 모드 탭 — 종이 대진표의 「경기 순서표」와 「체급별 대진표」 두 장에 대응한다 */
const VIEW_MODE_TABS: { mode: BracketViewMode; label: string }[] = [
  { mode: "TREE", label: "체급별 대진표" },
  { mode: "ALL", label: "전체 대진표" },
  { mode: "ORDER", label: "경기 순서" },
]

export default function BracketPage() {
  const {
    tournaments,
    tournamentId,
    summaries,
    matchOrders,
    allBrackets,
    detail,
    viewMode,
    isLoaded,
    errorMessage,
    selectTournament,
    selectViewMode,
    openDetail,
    closeDetail,
  } = useBracket()

  const isTreeMode = viewMode === "TREE"
  const isAllMode = viewMode === "ALL"
  const isEmpty =
    isLoaded && !errorMessage && tournamentId && summaries.length === 0
  const summarySportGroups = groupBySport(summaries)
  const detailMatchCount =
    detail?.rounds.reduce((count, round) => count + round.matches.length, 0) ??
    0

  function onChangeTournament(event: ChangeEvent<HTMLSelectElement>) {
    void selectTournament(event.target.value)
  }

  function onClickCloseDetail() {
    closeDetail()
  }

  return (
    <section className="app-page">
      <div className="app-page-head">
        <p className="app-txt-eyebrow text-app-primary-600">BRACKET</p>
        <h1 className="app-txt-h1 mt-3">대진표</h1>
        <p className="app-txt-body2 mt-3 leading-6 text-app-text-300">
          대회 전에 편성한 대진표입니다. 경기 번호는 종목마다 1번부터 매기며,
          경기 결과 · 승자는 현장에서 진행합니다.
        </p>
      </div>

      <div className="flex max-w-md flex-col gap-1.5">
        <label
          htmlFor="bracket-tournament"
          className="app-label-sub"
        >
          대회 선택
        </label>
        <select
          id="bracket-tournament"
          value={tournamentId}
          onChange={onChangeTournament}
          className="focus-ring app-field"
        >
          <option value="">대회를 선택하세요</option>
          {tournaments.map((tournament) => (
            <option
              key={tournament.id}
              value={tournament.id}
            >
              {tournament.name}
            </option>
          ))}
        </select>
      </div>

      {!isLoaded && <Loading label="대진표를 불러오는 중" />}

      {errorMessage && (
        <p
          role="alert"
          className="app-alert app-alert-error mt-6"
        >
          {errorMessage}
        </p>
      )}

      {isEmpty && <EmptyMessage message="아직 편성된 대진표가 없습니다." />}

      {summaries.length > 0 && (
        <div
          role="tablist"
          aria-label="대진표 보기 방식"
          className="mt-8 flex gap-2"
        >
          {VIEW_MODE_TABS.map((tab) => (
            <button
              key={tab.mode}
              type="button"
              role="tab"
              aria-selected={viewMode === tab.mode}
              onClick={() => selectViewMode(tab.mode)}
              className={
                viewMode === tab.mode
                  ? "focus-ring app-btn app-btn-brand app-btn-sm"
                  : "focus-ring app-btn app-btn-outline app-btn-sm"
              }
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {isAllMode && summaries.length > 0 && (
        <section className="mt-6">
          <h2 className="app-txt-h3">
            전체 대진표 · 체급 {allBrackets.length}개
          </h2>
          <p className="app-txt-body2 mt-1.5 text-app-text-200">
            종목별로 모든 대진표를 체급 순으로 펼쳐 놓았습니다. 경기 번호는
            종목마다 1번부터입니다.
          </p>

          <div className="mt-4">
            <BracketAllTrees brackets={allBrackets} />
          </div>
        </section>
      )}

      {viewMode === "ORDER" && summaries.length > 0 && (
        <section className="mt-6">
          <h2 className="app-txt-h3">
            경기 순서 · 전체 {matchOrders.length}경기
          </h2>
          <p className="app-txt-body2 mt-1.5 text-app-text-200">
            종목별 경기 순서입니다. 체급을 누르면 그 체급의 대진표로 이동합니다.
          </p>

          <div className="mt-4">
            <BracketOrderTable
              matchOrders={matchOrders}
              onSelectBracket={openDetail}
            />
          </div>
        </section>
      )}

      {isTreeMode && !detail && summaries.length > 0 && (
        <div className="mt-6 flex flex-col gap-10">
          {summarySportGroups.map((sportGroup) => (
            <section
              key={sportGroup.sportLabel}
              aria-label={`${sportGroup.sportLabel} 체급`}
            >
              <BracketSportHeading
                sportLabel={sportGroup.sportLabel}
                description={`체급 ${sportGroup.items.length}개`}
                level="h2"
              />

              <ul className="app-grid-hairline mt-4 list-none md:grid-cols-2 xl:grid-cols-3">
                {sportGroup.items.map((summary) => (
                  <li
                    key={summary.id}
                    className="flex flex-col"
                  >
                    <h3 className="app-txt-h3">{summary.weightClassName}</h3>
                    <p className="app-txt-body2 mt-2 font-bold text-app-text-300">
                      {summary.sizeLabel}
                    </p>

                    <p className="app-txt-body2 mt-3 text-app-text-200">
                      {summary.divisionLabel} · {summary.genderLabel}
                    </p>
                    <p className="app-txt-body2 mt-1 text-app-text-200">
                      선수 {summary.playerCount}명 · 경기 {summary.matchCount}개
                    </p>

                    <button
                      type="button"
                      onClick={() => openDetail(summary.id)}
                      className="focus-ring app-btn app-btn-brand app-btn-block mt-5"
                    >
                      대진표 보기
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {isTreeMode && detail && (
        <section className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="app-txt-h2">
                {detail.weightClassName} {detail.sizeLabel}
              </h2>
              <p className="app-txt-body2 mt-1.5 text-app-text-200">
                {detail.sportLabel} · {detail.divisionLabel} ·{" "}
                {detail.genderLabel} · 선수 {detail.slotCount}자리 · 경기{" "}
                {detailMatchCount}개
              </p>
            </div>
            <button
              type="button"
              onClick={onClickCloseDetail}
              className="focus-ring app-btn app-btn-outline"
            >
              ← 체급 목록으로
            </button>
          </div>

          <div className="mt-6 overflow-x-auto pb-4">
            <BracketTree rounds={detail.rounds} />
          </div>
        </section>
      )}
    </section>
  )
}
